import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ExpenseContext = createContext();
export const useExpenses = () => useContext(ExpenseContext);

export const ExpenseProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  const mountedRef = useRef(true);

  const EXPENSES_ENDPOINT = "/expenses";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- MOUNT SAFETY ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetExpenses = (value) => {
    if (mountedRef.current) setExpenses(value);
  };

  /* ---------------- FETCH ---------------- */
  const fetchExpenses = useCallback(async () => {
    if (!token) return;

    setLoading(true);

    try {
      const res = await fetch(`${baseURL}${EXPENSES_ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(payload?.message || "Fetch failed");

      const items =
        payload?.data || payload?.expenses || payload || [];

      safeSetExpenses(items);

      return payload;
    } catch (err) {
      console.error("[Expenses] fetch error:", err);

      if (err.message?.toLowerCase().includes("token")) {
        logout?.();
      }

      safeSetExpenses([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL, logout]);

  /* ---------------- ADD ---------------- */
  const addExpense = useCallback(async (data) => {
    try {
      const res = await fetch(`${baseURL}${EXPENSES_ENDPOINT}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(payload?.message || "Create failed");

      await fetchExpenses();
      return payload;
    } catch (err) {
      console.error("[Expenses] add error:", err);
      throw err;
    }
  }, [token, baseURL, fetchExpenses]);

  /* ---------------- UPDATE ---------------- */
  const updateExpense = useCallback(async (id, data) => {
    try {
      const res = await fetch(`${baseURL}${EXPENSES_ENDPOINT}/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(payload?.message || "Update failed");

      await fetchExpenses();
      return payload;
    } catch (err) {
      console.error("[Expenses] update error:", err);
      throw err;
    }
  }, [token, baseURL, fetchExpenses]);

  /* ---------------- DELETE ---------------- */
  const deleteExpense = useCallback(async (id) => {
    try {
      const res = await fetch(`${baseURL}${EXPENSES_ENDPOINT}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          delete_audit_id: adminId,
        }),
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(payload?.message || "Delete failed");

      safeSetExpenses((prev) =>
        prev.filter((e) => String(e.id) !== String(id))
      );

      return payload;
    } catch (err) {
      console.error("[Expenses] delete error:", err);
      throw err;
    }
  }, [token, baseURL, adminId]);

  /* ---------------- STATUS (OPTIMISTIC SAFE) ---------------- */
  const toggleStatus = useCallback(async (id, currentStatus) => {
    const newStatus =
      currentStatus === "active" ? "inactive" : "active";

    try {
      // optimistic update
      safeSetExpenses((prev) =>
        prev.map((e) =>
          String(e.id) === String(id)
            ? { ...e, status: newStatus }
            : e
        )
      );

      const res = await fetch(
        `${baseURL}${EXPENSES_ENDPOINT}/${id}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            status: newStatus,
            update_audit_id: adminId,
          }),
        }
      );

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(payload?.message || "Status failed");

      return payload;
    } catch (err) {
      console.error("[Expenses] status error:", err);

      // rollback
      safeSetExpenses((prev) =>
        prev.map((e) =>
          String(e.id) === String(id)
            ? { ...e, status: currentStatus }
            : e
        )
      );

      throw err;
    }
  }, [token, baseURL, adminId]);

  /* ---------------- INIT LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchExpenses().catch(() => {});
  }, [isReady, token, fetchExpenses]);

  return (
    <ExpenseContext.Provider
      value={{
        expenses,
        loading,
        fetchExpenses,
        addExpense,
        updateExpense,
        deleteExpense,
        toggleStatus,
      }}
    >
      {children}
    </ExpenseContext.Provider>
  );
};